// Shapes confirmed against QR-CODE-App/payments-client-kickstart (branch
// develop, src/repositories/PaymentsRepository.ts) and cross-checked against
// this project's actual deployed PMS OpenAPI spec
// (payments-service-dev.mgmt-efuturemedia.com/api/payments/v1/docs/json).

export enum PmsPaymentProviderEnum {
	STRIPE = 'stripe',
	// Real PMS value for a PayPal-backed subscription — the generic kickstart
	// example omits this from its own PaymentProviderEnum.
	PAYPAL_SUBSCRIPTION = 'paypal_subscription',
	NMI = 'nmi',
	ADYEN = 'adyen',
	PAYRAILS = 'payrails',
}

export enum PmsSubscriptionStatusEnum {
	// Internal — never returned by the API directly, only via async events.
	INITIAL = 'initial',
	INCOMPLETE = 'incomplete',
	// Public — returned by the API and handled by the app.
	ACTIVE = 'active',
	CANCELLED = 'cancelled',
	EXPIRED = 'expired',
}

export type PmsAdyenSdkEnvironment =
	| 'test'
	| 'live'
	| 'live-us'
	| 'live-au'
	| 'live-apse'
	| 'live-in'

export type PmsStripePublicConfiguration = {
	publicKey: string
}

export type PmsNmiPublicConfiguration = {
	publicTokenizationKey: string
	publicCheckoutKey?: string | null
}

export type PmsAdyenPublicConfiguration = {
	publicClientKey: string
	sdkEnvironment: PmsAdyenSdkEnvironment
}

// `@payrails/web-sdk` isn't a dependency of this project yet (the Payrails vs.
// direct-Stripe decision is still open on PM-3070), so this is typed loosely
// instead of importing the SDK's own `PayrailsEnvironment` enum for one field.
export type PmsPayrailsPublicConfiguration = {
	sdkEnvironment: string
}

export type PmsPaypalPublicConfiguration = {
	clientId: string
}

export type PmsProviderAccountPublicConfiguration =
	| PmsStripePublicConfiguration
	| PmsNmiPublicConfiguration
	| PmsAdyenPublicConfiguration
	| PmsPayrailsPublicConfiguration
	| Record<string, never>

export type PmsProviderAccount = {
	id: string
	provider: PmsPaymentProviderEnum
	providerId: string
	name: string
	countryCode: string
	configuration: PmsProviderAccountPublicConfiguration
	currentDescriptor?: string | null
}

export type PmsPaypalSubscriptionProviderAccount = Omit<
	PmsProviderAccount,
	'provider' | 'configuration'
> & {
	provider: PmsPaymentProviderEnum.PAYPAL_SUBSCRIPTION
	configuration: PmsPaypalPublicConfiguration
}

export type PmsPaypalSubscriptionPrice = {
	id: string
	providerId: string
	providerAccount: PmsPaypalSubscriptionProviderAccount
}

export type PmsPrice = {
	id: string
	currency: string
	amount: number
	trialAmount: number
	finalTrialAmount?: number
	trialDays: number
	duration?: number
	durationUnits?: string
	provider: PmsPaymentProviderEnum
	providerId: string
	providerAccountId: string
	isDefault: boolean
	providerAccount: PmsProviderAccount
	paypalSubscriptionPrice?: PmsPaypalSubscriptionPrice | null
	// Migrated from qr-api's Price.name — a headline "title price" override
	// (e.g. the monthly-equivalent for an annual plan, "19" for a $228/yr
	// plan), independent of `amount`. Confirmed against real data, 2026-08-10.
	metadata?: { name?: string }
}

// TODO(PMS-MIGRATION): these are qr-api's original translation keys and
// flags, migrated verbatim into PMS's product.metadata during seeding —
// confirmed against real data 2026-08-10. Components reading
// product.name/.description/.priceTitle/.recurrentName/.isFavourite need to
// read these instead, not wait on anything from Payments.
export type PmsProductMetadata = {
	isDefault: boolean
	isFavourite: boolean
	// Legacy qr-api priority — PMS's own top-level `priority` below is the
	// new sequential one (per Alex, Slack 2026-08-07).
	originalPriority: number
	nameTranslationKey: string
	priceTitleTranslationKey: string
	descriptionTranslationKey: string
	recurrentNameTranslationKey: string
	campaignOrigin?: string
	discountPercentage?: number | string
}

export type PmsProductWithAllPrices = {
	id: string
	sku: string
	prices: PmsPrice[]
	campaign: string
	// TODO(PMS-MIGRATION): confirmed present on the real response, not yet used.
	priority?: number
	metadata?: PmsProductMetadata
}

export type PmsProductWithOnePrice = Omit<PmsProductWithAllPrices, 'prices'> & {
	price: PmsPrice
}

export type PmsUpsellPrice = Omit<
	PmsPrice,
	'trialAmount' | 'trialDays' | 'finalTrialAmount'
>

export type PmsUpsellProduct = {
	id: string
	sku: string
	price: PmsUpsellPrice
	campaign: string
	priority: number
}

export type PmsSubscription = {
	id: string
	provider: PmsPaymentProviderEnum
	status: PmsSubscriptionStatusEnum
	onTrial: boolean
	providerSubscriptionId: string
	expiresAt: string
	createdAt: string
	cancelledAt?: string
	cancellationReason?: string | null
	nextPaymentAttemptAt?: string
	product: PmsProductWithOnePrice
}
